/**
 * UTM Parameter Capture & Retrieval
 *
 * Captures UTM parameters from the URL on page load and persists them
 * in sessionStorage so they survive client-side navigation but expire
 * when the tab closes.
 *
 * Usage:
 *   captureUtmParams()        — call once on app mount
 *   getTrafficSource()        — returns the traffic_source object for Firestore / Stripe
 */

import {
  reconcileAduReportAcquisitionIdentity,
  resolveDurableReportMessageKey,
  resolvePublicSurfacePath,
} from '../config/publicSurfaces';
import {
  resolveFlowMeta,
  resolvePublicFlowFromSearchParams,
  resolveRegisteredFlowIdentity,
} from '../config/reportConversionFlowRegistry';
import {
  canTrackMetaPixel,
  trackMetaPixelEvent,
} from '../consent/metaPixelTag';
import {
  getConsentSnapshot,
  runWhenConsentReady,
} from '../privacy/cookiebot';

export { canTrackMetaPixel };

// The frozen Property Check flow handoff is also carried on the report checkout
// route. Public Report routes resolve their applied flow separately below.
const FLOW_HANDOFF_PATHNAME = '/checkout/report';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const GOOGLE_CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid'];
const CLICK_ID_KEYS = [...GOOGLE_CLICK_ID_KEYS, 'fbclid'];
// `asid` is the cross-page handoff param appended to landing-page CTA URLs by the
// ported landing shell (src/lib/landingAttribution.ts) — it must win so the session
// minted on the landing page is the one that reaches checkout and Stripe.
const ATTRIBUTION_SESSION_ID_PARAMS = ['asid', 'attribution_session_id', 'attributionSessionId'];
const ATTRIBUTION_VARIANT_PARAMS = ['variant'];
const MESSAGE_KEY_PARAMS = ['messageKey', 'message_key'];
const MESSAGE_KEY_APPLIED_PARAMS = ['messageKeyApplied', 'message_key_applied'];
const OFFER_KEY_PARAMS = ['offerKey', 'offer_key'];
const AUDIENCE_KEY_PARAMS = ['audienceKey', 'audience_key'];
const FLOW_KEY_PARAMS = ['flow_key', 'flowKey'];
const FLOW_VERSION_PARAMS = ['flow_version', 'flowVersion'];
const STORAGE_PREFIX = 'adu_utm_';
const FB_CLICK_ID_KEY = `${STORAGE_PREFIX}fbclid`;
const FB_CLICK_TIMESTAMP_KEY = `${STORAGE_PREFIX}fbclid_timestamp`;
const ATTRIBUTION_SESSION_ID_KEY = `${STORAGE_PREFIX}attribution_session_id`;
const ATTRIBUTION_VARIANT_KEY = `${STORAGE_PREFIX}variant`;
const MESSAGE_KEY_KEY = `${STORAGE_PREFIX}message_key`;
const MESSAGE_KEY_APPLIED_KEY = `${STORAGE_PREFIX}message_key_applied`;
const OFFER_KEY_KEY = `${STORAGE_PREFIX}offer_key`;
const AUDIENCE_KEY_KEY = `${STORAGE_PREFIX}audience_key`;
const PRODUCT_KEY_KEY = `${STORAGE_PREFIX}product_key`;
const CARTRIDGE_KEY_KEY = `${STORAGE_PREFIX}cartridge_key`;
const SURFACE_KEY_KEY = `${STORAGE_PREFIX}surface_key`;
const EXPERIENCE_DISPLAY_NAME_KEY = `${STORAGE_PREFIX}experience_display_name`;
const TEMPLATE_ID_KEY = `${STORAGE_PREFIX}template_id`;
const LANDING_ID_KEY = `${STORAGE_PREFIX}landing_id`;
const DESTINATION_ID_KEY = `${STORAGE_PREFIX}destination_id`;
const FLOW_KEY_KEY = `${STORAGE_PREFIX}flow_key`;
const FLOW_VERSION_KEY = `${STORAGE_PREFIX}flow_version`;
const EDITION_ID_KEY = `${STORAGE_PREFIX}edition_id`;
const RENDER_ENGINE_KEY = `${STORAGE_PREFIX}render_engine`;

const ACQUISITION_IDENTITY_FIELDS = Object.freeze([
  ['productKey', 'product_key', PRODUCT_KEY_KEY],
  ['cartridgeKey', 'cartridge_key', CARTRIDGE_KEY_KEY],
  ['surfaceKey', 'surface_key', SURFACE_KEY_KEY],
  ['experienceDisplayName', 'experience_display_name', EXPERIENCE_DISPLAY_NAME_KEY],
  ['templateId', 'template_id', TEMPLATE_ID_KEY],
  ['landingId', 'landing_id', LANDING_ID_KEY],
  ['destinationId', 'destination_id', DESTINATION_ID_KEY],
  ['flowKey', 'flow_key', FLOW_KEY_KEY],
  ['flowVersion', 'flow_version', FLOW_VERSION_KEY],
  ['editionId', 'edition_id', EDITION_ID_KEY],
  ['renderEngine', 'render_engine', RENDER_ENGINE_KEY],
]);

function normalizeAttributionValue(value, maxLength = 180) {
  if (value === undefined || value === null) return '';
  const truncated = String(value).trim().slice(0, maxLength);
  if (!truncated || truncated.includes('@')) return '';
  return truncated.replace(/[^a-zA-Z0-9._:-]/g, '');
}

function readFirstParam(params, keys) {
  for (const key of keys) {
    const value = normalizeAttributionValue(params.get(key));
    if (value) return value;
  }
  return '';
}

// Campaign keys (message/offer/audience) fold to lowercase so the downstream
// vocabulary matches the landing shell's normalizeKey form — `Potential-Unlocked`
// and `potential-unlocked` must group as one arm. Session ids and variant keep
// their existing case behavior (live campaign variants already join on it).
function readFirstKeyParam(params, keys) {
  const value = readFirstParam(params, keys);
  return value ? value.toLowerCase() : '';
}

function readSessionValue(key) {
  try {
    return normalizeAttributionValue(sessionStorage.getItem(key));
  } catch (_e) {
    return '';
  }
}

function writeSessionValue(key, value) {
  try {
    if (value) {
      sessionStorage.setItem(key, value);
    }
  } catch (_e) {
    // Storage can be unavailable in strict browser modes.
  }
}

function removeSessionValue(key) {
  try {
    sessionStorage.removeItem(key);
  } catch (_e) {
    // Storage can be unavailable in strict browser modes.
  }
}

// A URL carrying an attribution session id different from the stored one is a NEW
// landing handoff — the URL defines the complete campaign spine. Merging stored
// leftovers into it can pair a fresh messageKey with a stale messageKeyApplied and
// credit an arm that never rendered this session.
function isNewAttributionHandoff(urlAttribution) {
  return !!urlAttribution.attributionSessionId &&
    urlAttribution.attributionSessionId !== readSessionValue(ATTRIBUTION_SESSION_ID_KEY);
}

function createAttributionSessionId() {
  try {
    if (window.crypto?.randomUUID) {
      return `attr_${window.crypto.randomUUID()}`;
    }
  } catch (_e) {
    // Fall through to timestamp fallback.
  }

  return `attr_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function readUrlAttributionContext() {
  try {
    const params = new URLSearchParams(window.location.search || '');
    const messageKeyAlias = readFirstKeyParam(params, MESSAGE_KEY_PARAMS);
    const messageKeyAppliedAlias = readFirstKeyParam(
      params,
      MESSAGE_KEY_APPLIED_PARAMS,
    );
    return {
      attributionSessionId: readFirstParam(params, ATTRIBUTION_SESSION_ID_PARAMS),
      variant: readFirstParam(params, ATTRIBUTION_VARIANT_PARAMS),
      messageKey:
        resolveDurableReportMessageKey(messageKeyAlias) || messageKeyAlias,
      messageKeyApplied:
        resolveDurableReportMessageKey(messageKeyAppliedAlias) ||
        messageKeyAppliedAlias,
      offerKey: readFirstKeyParam(params, OFFER_KEY_PARAMS),
      audienceKey: readFirstKeyParam(params, AUDIENCE_KEY_PARAMS),
      // Public Report routes persist their APPLIED paid-eligible flow, including
      // the default D fallback for missing/rejected selectors. Checkout accepts
      // only its frozen complete registered handoff. All other routes are inert.
      ...readUrlFlowIdentity(params),
    };
  } catch (_e) {
    return {};
  }
}

// Resolve a complete flow pair according to the current route's authority:
//   • /checkout/report accepts only an exact registered frozen handoff;
//   • the six public Report routes persist the public resolver's APPLIED flow,
//     including D for missing, incomplete, unknown, or ineligible selectors;
//   • every other route returns no pair and cannot make URL flow params durable.
function readUrlFlowIdentity(params) {
  let pathname = '';
  try {
    pathname = window.location?.pathname || '';
  } catch (_e) {
    pathname = '';
  }

  if (pathname === FLOW_HANDOFF_PATHNAME) {
    const { flowKey, flowVersion } = resolveRegisteredFlowIdentity(
      readFirstParam(params, FLOW_KEY_PARAMS),
      readFirstParam(params, FLOW_VERSION_PARAMS),
    );
    return { flowKey: flowKey || '', flowVersion: flowVersion || '' };
  }

  if (!resolvePublicSurfacePath(pathname)) {
    return { flowKey: '', flowVersion: '' };
  }

  // A public route's provider is the authority after mount (including the
  // staging-only toolbar). Reapply URL/default selection only when this URL
  // actually attempted a selector or begins a fresh attribution handoff;
  // otherwise leave the provider-synced stored pair intact.
  const hasFlowSelector = [...FLOW_KEY_PARAMS, ...FLOW_VERSION_PARAMS]
    .some((key) => params.has(key));
  const urlAttributionSessionId = readFirstParam(
    params,
    ATTRIBUTION_SESSION_ID_PARAMS,
  );
  const isFreshAttributionHandoff = !!urlAttributionSessionId
    && urlAttributionSessionId !== readSessionValue(ATTRIBUTION_SESSION_ID_KEY);
  if (!hasFlowSelector && !isFreshAttributionHandoff) {
    return { flowKey: '', flowVersion: '' };
  }

  const flowKey = resolvePublicFlowFromSearchParams(params.toString());
  const flowVersion = resolveFlowMeta(flowKey).flowVersion;
  return { flowKey, flowVersion };
}

function readResolvedPublicSurfaceAttribution() {
  try {
    const resolved = resolvePublicSurfacePath(window.location.pathname || '');
    if (!resolved) return {};

    return {
      messageKey: resolved.messageKey,
      messageKeyApplied: resolved.messageKeyApplied,
      productKey: resolved.productKey,
      cartridgeKey: resolved.cartridgeKey,
      surfaceKey: resolved.surfaceKey,
      experienceDisplayName: resolved.experienceDisplayName,
      templateId: resolved.templateId,
      landingId: resolved.landingId,
      destinationId: resolved.destinationId,
      renderEngine: resolved.renderEngine,
    };
  } catch (_e) {
    return {};
  }
}

/**
 * Read UTM params from the current URL and stash them in sessionStorage.
 * Also stores the referrer and the full landing URL.
 * Only writes if at least one attribution param is present (avoids overwriting
 * a previous capture when the user navigates internally).
 */
export function captureUtmParams() {
  try {
    const params = new URLSearchParams(window.location.search);

    // Check whether any attribution param exists in the current URL
    const hasUtm = UTM_KEYS.some((key) => params.has(key));
    const fbclid = params.get('fbclid');
    const googleClickIds = Object.fromEntries(
      GOOGLE_CLICK_ID_KEYS
        .map((key) => [key, params.get(key)])
        .filter(([, value]) => value)
    );
    // A valid public route is authoritative for Product/Message/template/
    // landing/destination identity. Query parameters may carry session/campaign
    // context, but can never override the route's approved acquisition spine.
    const urlAttribution = {
      ...readUrlAttributionContext(),
      ...readResolvedPublicSurfaceAttribution(),
    };
    const hasAttribution = Object.values(urlAttribution).some(Boolean);
    if (!hasUtm && !fbclid && Object.keys(googleClickIds).length === 0 && !hasAttribution) return;

    // Store each UTM value
    UTM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) {
        sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, value);
      }
    });

    if (fbclid) {
      sessionStorage.setItem(FB_CLICK_ID_KEY, fbclid);
      sessionStorage.setItem(FB_CLICK_TIMESTAMP_KEY, Date.now().toString());
    }

    GOOGLE_CLICK_ID_KEYS.forEach((key) => {
      const value = googleClickIds[key];
      if (value) {
        sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, value);
      }
    });

    // Store referrer (only on very first capture)
    if (!sessionStorage.getItem(`${STORAGE_PREFIX}referrer`)) {
      sessionStorage.setItem(`${STORAGE_PREFIX}referrer`, document.referrer || '');
    }

    // Always store the landing URL that carried UTM params
    sessionStorage.setItem(`${STORAGE_PREFIX}landing_url`, window.location.href);
    sessionStorage.setItem(`${STORAGE_PREFIX}timestamp`, new Date().toISOString());

    const newHandoff = isNewAttributionHandoff(urlAttribution);
    const attributionSessionId = urlAttribution.attributionSessionId ||
      readSessionValue(ATTRIBUTION_SESSION_ID_KEY) ||
      createAttributionSessionId();
    writeSessionValue(ATTRIBUTION_SESSION_ID_KEY, attributionSessionId);

    const spineKeys = [
      [ATTRIBUTION_VARIANT_KEY, urlAttribution.variant],
      [MESSAGE_KEY_KEY, urlAttribution.messageKey],
      [MESSAGE_KEY_APPLIED_KEY, urlAttribution.messageKeyApplied],
      [OFFER_KEY_KEY, urlAttribution.offerKey],
      [AUDIENCE_KEY_KEY, urlAttribution.audienceKey],
      ...ACQUISITION_IDENTITY_FIELDS.map(
        ([propertyName, , storageKey]) => [
          storageKey,
          urlAttribution[propertyName],
        ],
      ),
    ];
    for (const [storageKey, urlValue] of spineKeys) {
      if (urlValue) {
        writeSessionValue(storageKey, urlValue);
      } else if (newHandoff) {
        removeSessionValue(storageKey);
      }
    }
  } catch (e) {
    // sessionStorage may be unavailable in rare edge cases — never block the app
    console.warn('UTM capture failed:', e);
  }
}

function getCookie(name) {
  try {
    const encodedName = `${encodeURIComponent(name)}=`;
    return document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith(encodedName))
      ?.slice(encodedName.length) || '';
  } catch (_e) {
    return '';
  }
}

function buildFbcFromStoredClickId() {
  try {
    const fbclid = sessionStorage.getItem(FB_CLICK_ID_KEY);
    if (!fbclid) return '';
    const timestamp = sessionStorage.getItem(FB_CLICK_TIMESTAMP_KEY) || Date.now().toString();
    return `fb.1.${timestamp}.${fbclid}`;
  } catch (_e) {
    return '';
  }
}

function getStoredClickIds() {
  const clickIds = {};

  GOOGLE_CLICK_ID_KEYS.forEach((key) => {
    const value = sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
    if (value) clickIds[key] = value;
  });

  const fbclid = sessionStorage.getItem(FB_CLICK_ID_KEY);
  if (fbclid) clickIds.fbclid = fbclid;

  return clickIds;
}

function getAttributionSessionId() {
  try {
    const existing = sessionStorage.getItem(ATTRIBUTION_SESSION_ID_KEY);
    if (existing) return existing;

    let id = '';
    try {
      id = window.crypto?.randomUUID ? `attr_${window.crypto.randomUUID()}` : '';
    } catch {
      id = '';
    }

    if (!id) {
      id = `attr_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    }

    sessionStorage.setItem(ATTRIBUTION_SESSION_ID_KEY, id);
    return id;
  } catch {
    return '';
  }
}

function currentHost() {
  try {
    return window.location?.hostname || '';
  } catch {
    return '';
  }
}

function isInternalAttributionTest(source = {}) {
  const host = currentHost();
  if (!host || !canTrackMetaPixel(host)) return true;

  return ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']
    .some((key) => String(source[key] || '').toLowerCase().includes('codex'));
}

function buildUploadEligibility(source = {}) {
  const internalTest = isInternalAttributionTest(source);
  const snapshot = getConsentSnapshot({ internalTest });
  return (({
    marketing_consent,
    do_not_sell,
    gpc,
    internal_test,
    ad_upload_eligible,
    upload_blocked_reason,
  }) => ({
    marketing_consent,
    do_not_sell,
    gpc,
    internal_test,
    ad_upload_eligible,
    ...(upload_blocked_reason && { upload_blocked_reason }),
  }))(snapshot);
}

/**
 * Build non-PII browser conversion metadata for Stripe metadata.
 * Returns data needed for browser/server event deduplication and Meta CAPI match quality.
 */
export function getConversionTrackingData() {
  try {
    const fbp = getCookie('_fbp');
    const fbc = getCookie('_fbc') || buildFbcFromStoredClickId();
    const clickIds = getStoredClickIds();
    const trafficSource = getTrafficSource() || {};
    const eligibility = buildUploadEligibility(trafficSource);
    const data = {
      event_source_url: window.location.href,
      landing_url: sessionStorage.getItem(`${STORAGE_PREFIX}landing_url`) || window.location.href,
      referrer: sessionStorage.getItem(`${STORAGE_PREFIX}referrer`) || document.referrer || '',
      user_agent: navigator.userAgent || '',
      attribution_session_id: getAttributionSessionId(),
      ...eligibility,
      ...(eligibility.ad_upload_eligible && fbp && { fbp }),
      ...(eligibility.ad_upload_eligible && fbc && { fbc }),
      ...(eligibility.ad_upload_eligible && Object.fromEntries(
        CLICK_ID_KEYS
          .map((key) => [key, clickIds[key]])
          .filter(([, value]) => value)
      )),
    };

    return data;
  } catch (_e) {
    return null;
  }
}

export function createConversionEventId() {
  try {
    if (window.crypto?.randomUUID) {
      return `purchase_${window.crypto.randomUUID()}`;
    }
  } catch (_e) {
    // Fall through to timestamp fallback.
  }

  return `purchase_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Make a resolved path-based landing message authoritative for downstream
 * attribution. Requested and applied keys remain separate so an unknown
 * future request can never receive credit for copy that did not render.
 *
 * The baseline `/report` route stores durable Message key `adu_standard`.
 * `default` remains an internal DTR alias and never enters this storage spine.
 */
export function syncLandingMessageAttribution({
  requestedMessageKey,
  appliedMessageKey,
  productKey,
  cartridgeKey,
  surfaceKey,
  experienceDisplayName,
  templateId,
  landingId,
  destinationId,
  flowKey,
  flowVersion,
  editionId,
  renderEngine,
} = {}) {
  const requestedAlias = normalizeAttributionValue(
    requestedMessageKey,
  ).toLowerCase();
  const appliedAlias = normalizeAttributionValue(
    appliedMessageKey,
  ).toLowerCase();
  const requested = resolveDurableReportMessageKey(requestedAlias) ||
    requestedAlias;
  const applied = resolveDurableReportMessageKey(appliedAlias) || appliedAlias;
  const identity = {
    productKey,
    cartridgeKey,
    surfaceKey,
    experienceDisplayName,
    templateId,
    landingId,
    destinationId,
    flowKey,
    flowVersion,
    editionId,
    renderEngine,
  };
  const reconciliation = reconcileAduReportAcquisitionIdentity({
    ...identity,
    messageKey: requested,
    messageKeyApplied: applied,
  });
  // ATOMIC MESSAGE+FLOW PAIRING: a report-identity claim that was attempted but
  // failed cross-field reconciliation must not let flowKey/flowVersion persist
  // alongside the wreckage — mirrors the server-side gate in
  // functions/createCheckoutSessionSimple.js's buildAttributionMetadata. A
  // context that never claimed a report identity at all is unaffected.
  const invalidReportIdentityClaim =
    reconciliation.isReportIdentityClaim && !reconciliation.identity;
  const supplementalIdentity = invalidReportIdentityClaim
    ? { editionId }
    : {
        flowKey,
        flowVersion,
        editionId,
      };
  const reconciledAttribution = reconciliation.isReportIdentityClaim
    ? { ...(reconciliation.identity || {}), ...supplementalIdentity }
    : {
        ...(requested && { messageKey: requested }),
        ...(applied && { messageKeyApplied: applied }),
        ...identity,
        ...supplementalIdentity,
      };
  const reconciledRequested = reconciledAttribution.messageKey || '';
  const reconciledApplied = reconciledAttribution.messageKeyApplied || '';

  if (reconciledRequested) {
    writeSessionValue(MESSAGE_KEY_KEY, reconciledRequested);
    getAttributionSessionId();
  } else {
    removeSessionValue(MESSAGE_KEY_KEY);
  }

  if (reconciledApplied) {
    writeSessionValue(MESSAGE_KEY_APPLIED_KEY, reconciledApplied);
  } else {
    removeSessionValue(MESSAGE_KEY_APPLIED_KEY);
  }

  for (const [propertyName, , storageKey] of ACQUISITION_IDENTITY_FIELDS) {
    const value = normalizeAttributionValue(
      reconciledAttribution[propertyName],
    );
    if (value) {
      writeSessionValue(storageKey, value);
    } else {
      removeSessionValue(storageKey);
    }
  }

  return {
    ...(reconciledRequested && { messageKey: reconciledRequested }),
    ...(reconciledApplied && { messageKeyApplied: reconciledApplied }),
    ...Object.fromEntries(
      ACQUISITION_IDENTITY_FIELDS
        .map(([propertyName]) => [
          propertyName,
          normalizeAttributionValue(reconciledAttribution[propertyName]),
        ])
        .filter(([, value]) => value),
    ),
  };
}

/**
 * Build the traffic_source object from sessionStorage.
 * Returns null if no UTM data was ever captured this session.
 */
export function getTrafficSource() {
  try {
    const source = {};
    let hasData = false;

    UTM_KEYS.forEach((key) => {
      const value = sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
      if (value) {
        source[key] = value;
        hasData = true;
      }
    });

    GOOGLE_CLICK_ID_KEYS.forEach((key) => {
      const value = sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
      if (value) {
        source[key] = value;
        hasData = true;
      }
    });

    const fbclid = sessionStorage.getItem(FB_CLICK_ID_KEY);
    if (fbclid) {
      source.fbclid = fbclid;
      hasData = true;
    }

    const attributionContext = getAttributionContext();
    if (attributionContext?.attributionSessionId) {
      source.attribution_session_id = attributionContext.attributionSessionId;
      hasData = true;
    }
    if (attributionContext?.variant) {
      source.variant = attributionContext.variant;
      hasData = true;
    }
    if (attributionContext?.messageKey) {
      source.message_key = attributionContext.messageKey;
      hasData = true;
    }
    if (attributionContext?.messageKeyApplied) {
      source.message_key_applied = attributionContext.messageKeyApplied;
      hasData = true;
    }
    for (const [propertyName, outputKey] of ACQUISITION_IDENTITY_FIELDS) {
      if (attributionContext?.[propertyName]) {
        source[outputKey] = attributionContext[propertyName];
        hasData = true;
      }
    }
    const offerKey = readSessionValue(OFFER_KEY_KEY);
    if (offerKey) {
      source.offer_key = offerKey;
      hasData = true;
    }
    const audienceKey = readSessionValue(AUDIENCE_KEY_KEY);
    if (audienceKey) {
      source.audience_key = audienceKey;
      hasData = true;
    }

    if (!hasData) return null;

    source.referrer = sessionStorage.getItem(`${STORAGE_PREFIX}referrer`) || '';
    source.landing_url = sessionStorage.getItem(`${STORAGE_PREFIX}landing_url`) || '';
    source.timestamp = sessionStorage.getItem(`${STORAGE_PREFIX}timestamp`) || '';
    source.attribution_session_id = getAttributionSessionId();
    Object.assign(source, buildUploadEligibility(source));

    return source;
  } catch (e) {
    console.warn('UTM retrieval failed:', e);
    return null;
  }
}

export function getAttributionContext() {
  // URL params win over stored values — same precedence as captureUtmParams. A CTA
  // handoff (?asid=...) must override any session id this app minted earlier, and
  // consumers can read the context BEFORE App's captureUtmParams effect has run
  // (child effects fire first), so falling back to storage-first here would emit
  // the stale id and split the attribution join. On a NEW handoff the URL defines
  // the complete spine — stored leftovers from a previous landing must not fill
  // fields the URL omits (a stale messageKeyApplied would credit the wrong arm).
  const urlAttribution = readUrlAttributionContext();
  const newHandoff = isNewAttributionHandoff(urlAttribution);
  const storedFallback = (storageKey) => (newHandoff ? '' : readSessionValue(storageKey));
  const attributionSessionId = urlAttribution.attributionSessionId ||
    readSessionValue(ATTRIBUTION_SESSION_ID_KEY);
  const variant = urlAttribution.variant ||
    storedFallback(ATTRIBUTION_VARIANT_KEY);
  const messageKeyAlias = urlAttribution.messageKey ||
    storedFallback(MESSAGE_KEY_KEY);
  const messageKey = resolveDurableReportMessageKey(messageKeyAlias) ||
    messageKeyAlias;
  // The arm that actually rendered on the landing page — join message performance on
  // this, never on messageKey (which is only what the URL requested).
  const messageKeyAppliedAlias = urlAttribution.messageKeyApplied ||
    storedFallback(MESSAGE_KEY_APPLIED_KEY);
  const messageKeyApplied = resolveDurableReportMessageKey(
    messageKeyAppliedAlias,
  ) || messageKeyAppliedAlias;
  const acquisitionIdentity = Object.fromEntries(
    ACQUISITION_IDENTITY_FIELDS
      .map(([propertyName, , storageKey]) => [
        propertyName,
        storedFallback(storageKey),
      ])
      .filter(([, value]) => value),
  );

  // On a valid public surface, route identity always wins over query or
  // previously stored values. On downstream checkout/pricing routes, the
  // same-tab stored identity carries the join forward.
  const resolvedPublicSurface =
    readResolvedPublicSurfaceAttribution();
  Object.assign(acquisitionIdentity, resolvedPublicSurface);

  // A frozen Property Check purchase handoff carries the flow pair on the
  // checkout URL, and must win over the stored flow (the preview rail rewrites
  // sessionStorage flow_key on every switch, so a B->C switch would otherwise
  // report B through the CTA but send C to Stripe/revenue). urlAttribution
  // already carries a route-scoped + registry-validated pair (readUrlFlowIdentity
  // returns both fields or neither), so this apply is atomic by construction.
  if (urlAttribution.flowKey && urlAttribution.flowVersion) {
    acquisitionIdentity.flowKey = urlAttribution.flowKey;
    acquisitionIdentity.flowVersion = urlAttribution.flowVersion;
  }

  const reconciliation = reconcileAduReportAcquisitionIdentity({
    ...acquisitionIdentity,
    messageKey: resolvedPublicSurface.messageKey || messageKey,
    messageKeyApplied:
      resolvedPublicSurface.messageKeyApplied || messageKeyApplied,
  });
  // ATOMIC MESSAGE+FLOW PAIRING: a report-identity claim that was attempted but
  // failed cross-field reconciliation must not let an independently-resolved
  // flow ride alongside the wreckage — mirrors the server-side gate in
  // functions/createCheckoutSessionSimple.js's buildAttributionMetadata. A
  // context that never claimed a report identity at all is unaffected; flow
  // selection is intentionally not part of the durable public route tuple
  // (`/report` plus its five Message routes are the only public identity) and
  // is preserved separately for the checkout and first-party event handoff.
  const invalidReportIdentityClaim =
    reconciliation.isReportIdentityClaim && !reconciliation.identity;
  const supplementalIdentity = Object.fromEntries(
    (invalidReportIdentityClaim
      ? ['editionId']
      : ['flowKey', 'flowVersion', 'editionId'])
      .map((propertyName) => [propertyName, acquisitionIdentity[propertyName]])
      .filter(([, value]) => value),
  );
  const reconciledAttribution = reconciliation.isReportIdentityClaim
    ? { ...(reconciliation.identity || {}), ...supplementalIdentity }
    : {
        ...(messageKey && { messageKey }),
        ...(messageKeyApplied && { messageKeyApplied }),
        ...acquisitionIdentity,
      };

  if (
    !attributionSessionId &&
    !variant &&
    Object.keys(reconciledAttribution).length === 0
  ) {
    return null;
  }

  return {
    ...(attributionSessionId && { attributionSessionId }),
    ...(variant && { variant }),
    ...reconciledAttribution,
  };
}

/**
 * Fire a Meta Pixel standard event.
 * Safe to call even if the pixel isn't loaded — silently no-ops.
 *
 * @param {string} eventName  Standard event name (e.g. 'Lead', 'Purchase', 'InitiateCheckout')
 * @param {Object} [params]   Optional event parameters
 */
export function trackMetaEvent(eventName, params, options) {
  try {
    if (!canTrackMetaPixel()) return;
    runWhenConsentReady('marketing', () => {
      trackMetaPixelEvent(eventName, params, options);
    });
  } catch {
    // Never block the app for tracking failures
  }
}
